import { useState } from "react";
import type { SxProps, Theme } from "@mui/material/styles";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import ButtonBase from "@mui/material/ButtonBase";

type Props = {
  userLabel: string;
  userEmail?: string;
  userNameSx?: SxProps<Theme>;
  userEmailSx?: SxProps<Theme>;
  avatarSx?: SxProps<Theme>;
  /** Show name/email beside the avatar (desktop). */
  showDetails?: boolean;
};

/** Far-right top-bar control: avatar (+ optional name); opens a menu with sign out. */
export default function UserAccountMenu({
  userLabel,
  userEmail,
  userNameSx,
  userEmailSx,
  avatarSx,
  showDetails = true,
}: Props) {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const initial = userLabel.charAt(0).toUpperCase() || "U";

  return (
    <>
      <ButtonBase
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label={t("navbar.accountMenu")}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderRadius: 1.25,
          px: showDetails ? 1 : 0.5,
          py: 0.5,
          textAlign: "left",
          maxWidth: 240,
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <Avatar sx={avatarSx}>{initial}</Avatar>
        {showDetails ? (
          <Box display={{ xs: "none", sm: "block" }} minWidth={0}>
            <Typography variant="body2" noWrap sx={userNameSx}>
              {userLabel}
            </Typography>
            {userEmail && userEmail !== userLabel ? (
              <Typography variant="caption" noWrap component="div" sx={userEmailSx}>
                {userEmail}
              </Typography>
            ) : null}
          </Box>
        ) : null}
      </ButtonBase>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 200 } } }}
      >
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography variant="subtitle2" noWrap>
            {userLabel}
          </Typography>
          {userEmail && userEmail !== userLabel ? (
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {userEmail}
            </Typography>
          ) : null}
        </Box>
        <MenuItem component={Link} to="/logout" onClick={() => setAnchor(null)}>
          <ListItemIcon>
            <Icon fontSize="small">logout</Icon>
          </ListItemIcon>
          <ListItemText>{t("navbar.logout")}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
